import React, { useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { PricingActions } from 'state/modules/pricing';
import { Box, Grid, Paper, Button, Container, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { buildUrl } from 'utils';
import Title from 'components/Title';
import Layout from 'components/Layout';
import { Formik, Field } from 'formik';
import useStyles from './TaxRuleset.styled';
import { useParams } from 'react-router-dom';
import { TextField } from 'components/FormFields';
import { TaxRulesetTable } from 'components/TaxRuleset';
import { AuthActions } from 'state/modules/auth';

const TaxRulesetCreate = ({ history }) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const formRef = useRef(null);
  const { id, action } = useParams();

  const fetchingCreateTaxRuleset = useSelector((state) => state.pricing.fetchingCreateTaxRuleset);
  const fetchedCreateTaxRuleset = useSelector((state) => state.pricing.fetchedCreateTaxRuleset);
  const fetchedUpdateTaxRuleset = useSelector((state) => state.pricing.fetchedUpdateTaxRuleset);
  const currentTaxRulesetId = useSelector((state) => state.pricing.currentTaxRulesetId);
  const { name, taxes, description } = useSelector((state) => state?.pricing?.currentTaxRuleset);
  const taxRulesetsEnabled = useSelector(
    (state) => state.config?.settings?.core?.pricing?.tax_rules_set?.enabled ?? true
  );

  useEffect(() => {
    if (fetchedCreateTaxRuleset || fetchedUpdateTaxRuleset) {
      history.push(buildUrl(`/tax-rulesets/${currentTaxRulesetId}`));
      dispatch(PricingActions.resetCurrentTaxRuleset());
    }
  }, [fetchedCreateTaxRuleset, fetchedUpdateTaxRuleset]); // eslint-disable-line

  useEffect(() => {
    if (!taxRulesetsEnabled) {
      return dispatch(AuthActions.unauthorized());
    }
    if (id && action) {
      dispatch(PricingActions.fetchTaxRulesetById(id));
    }

    return () => {
      dispatch(PricingActions.resetCurrentTaxRuleset());
    };
  }, [id, action]); // eslint-disable-line

  const isCloning = action === 'clone';
  const initialValues = {
    taxes: taxes || [],
    name: isCloning ? name && i18n.t('common.copy_of', { name }) : name,
    description: description,
  };

  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    description: Yup.string().nullable(),
    taxes: Yup.array(),
  });

  const onSubmit = (values) => {
    const data = { ...values };
    if (action === 'update' && id) {
      dispatch(PricingActions.fetchUpdateTaxRuleset(id, { ...data }));
    } else {
      dispatch(PricingActions.fetchCreateTaxRuleset(data));
    }
  };

  return (
    <Layout title={i18n.t('ruleset.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Title title={i18n.t(`tax_ruleset.${action || 'create'}`)} />
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  variant="contained"
                  data-cy="save-tax-ruleset-btn"
                  disabled={fetchingCreateTaxRuleset}
                  style={{ textTransform: 'capitalize' }}
                  onClick={() => formRef.current.submitForm()}
                >
                  {i18n.t('common.save')}
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={2} className={classes.paper}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h2" align="left">
                      <Box fontWeight="500" fontSize={14}>
                        {i18n.t('ruleset.ruleset_name')}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Formik
                      initialValues={initialValues}
                      validationSchema={formSchema}
                      enableReinitialize
                      innerRef={formRef}
                      onSubmit={onSubmit}
                    >
                      {({ handleSubmit, values, setFieldValue }) => (
                        <form noValidate onSubmit={handleSubmit}>
                          <Grid
                            container
                            spacing={2}
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                          >
                            <Grid item xs={12} sm={4}>
                              <Field
                                name="name"
                                margin="normal"
                                component={TextField}
                                inputProps={{ autoComplete: 'nope' }}
                                label={i18n.t('tax_ruleset.tax_ruleset_name')}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Field
                                margin="normal"
                                name="description"
                                component={TextField}
                                label={i18n.t('common.description')}
                                inputProps={{ autoComplete: 'nope' }}
                              />
                            </Grid>

                            <Grid item xs={12} style={{ marginTop: 25 }}>
                              <TaxRulesetTable
                                id={id}
                                action={action}
                                onDelete={(index) => {
                                  setFieldValue(
                                    'taxes',
                                    values.taxes.filter((_, i) => i !== index)
                                  );
                                }}
                                onUpdate={(taxes) => {
                                  setFieldValue('taxes', taxes);
                                }}
                                taxes={values.taxes || []}
                              />
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </Formik>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default withRouter(TaxRulesetCreate);
